/**
 * Состояние автоматического разбора: запуск, прогресс, результат, применение
 * (docs/ANALYSIS.md). Прогресс приходит по WebSocket, поллинг — фолбэк,
 * ровно как у рендера.
 *
 * Разбор идёт минутами, поэтому идентификатор задачи переживает перезагрузку
 * страницы: иначе закрытая вкладка означала бы потерянный час работы GPU.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import * as api from '../api/client'
import { ApiError, describeError } from '../api/errors'
import { reportRequestFailure } from '../api/serverStatus'
import type { ProjectSocket } from '../api/ws'
import type {
  Analysis,
  AnalysisOptions,
  AnalysisResult,
  ProjectFull,
} from '../api/types'
import { readPref, writePref } from '../lib/prefs'
import { buildApplyPlan, draftFromResult } from './draft'
import type { AnalysisDraft } from './draft'

const POLL_INTERVAL_MS = 2000

export type ApplyChoices = {
  /** Создать дорожки me/dialogue из результата разбора. */
  tracks: boolean
  /** true — заменить текущую разметку, false — добавить к существующей. */
  replaceExisting: boolean
  /** Перевести проект в режим полного дубляжа. */
  setModeReplace: boolean
}

export type AnalysisController = {
  analysis: Analysis | null
  draft: AnalysisDraft | null
  /** Параметры запуска — нужны, чтобы объяснить пустой перевод. */
  options: AnalysisOptions | null
  starting: boolean
  cancelling: boolean
  fetchingResult: boolean
  applying: boolean
  error: string | null
  start: (options: AnalysisOptions) => Promise<void>
  cancel: () => Promise<void>
  /** Повторно запросить результат, если он не догрузился. */
  reloadResult: () => Promise<void>
  setDraft: (next: AnalysisDraft) => void
  /** Отбросить черновик и забыть задачу. */
  dismiss: () => void
  apply: (choices: ApplyChoices, videoDuration: number) => Promise<ProjectFull | null>
}

function prefKey(projectId: string): string {
  return `ozvuchka.analysis.${projectId}`
}

type StoredAnalysis = { id: string; translate: boolean }

function readStored(projectId: string): StoredAnalysis | null {
  const raw = readPref(prefKey(projectId))
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as unknown
    if (typeof parsed !== 'object' || parsed === null) return null
    const id = (parsed as { id?: unknown }).id
    if (typeof id !== 'string' || !id) return null
    return { id, translate: (parsed as { translate?: unknown }).translate !== false }
  } catch {
    return null
  }
}

function store(projectId: string, value: StoredAnalysis | null): void {
  writePref(prefKey(projectId), value === null ? '' : JSON.stringify(value))
}

function isFinished(status: Analysis['status']): boolean {
  return status === 'done' || status === 'error' || status === 'cancelled'
}

/**
 * 400 при заданных параметрах сегментации — почти наверняка бэкенд их ещё
 * не знает (docs/SEGMENTATION.md, «Параметры»): блок необязательный и
 * появился позже самого разбора.
 */
function segmentationRejected(err: unknown, options: AnalysisOptions): string | null {
  if (!options.segmentation) return null
  if (err instanceof ApiError && err.status === 400) {
    return 'Сервер отклонил параметры сегментации — бэкенд, вероятно, ещё не обновлён. Запустите разбор со значениями по умолчанию.'
  }
  return null
}

/** Бэкенд ещё не знает про разбор — это не поломка, а незавершённый этап. */
function missingEndpointMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null
  if (err.status === 404 || err.status === 405 || err.status === 501) {
    return 'Бэкенд не отвечает на эндпоинты разбора — сервер ещё не обновлён.'
  }
  return null
}

export function useAnalysis(
  projectId: string,
  socket: ProjectSocket | null,
): AnalysisController {
  const [analysis, setAnalysis] = useState<Analysis | null>(null)
  const [draft, setDraftState] = useState<AnalysisDraft | null>(null)
  const [options, setOptions] = useState<AnalysisOptions | null>(null)
  const [starting, setStarting] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [fetchingResult, setFetchingResult] = useState(false)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mountedRef = useRef(true)
  const analysisRef = useRef<Analysis | null>(null)
  const translateRef = useRef(true)
  analysisRef.current = analysis

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Смена проекта — полный сброс: чужой черновик показывать нельзя.
  useEffect(() => {
    setAnalysis(null)
    setDraftState(null)
    setOptions(null)
    setError(null)
  }, [projectId])

  // ------------------------------------------------- восстановление задачи

  useEffect(() => {
    const stored = readStored(projectId)
    if (!stored) return
    translateRef.current = stored.translate
    const controller = new AbortController()
    void api
      .getAnalysis(stored.id, controller.signal)
      .then((found) => {
        if (!mountedRef.current || controller.signal.aborted) return
        setAnalysis(found)
        if (found.status === 'cancelled' || found.status === 'error') store(projectId, null)
      })
      .catch(() => {
        // Отмена запроса при уходе со страницы — не повод забывать задачу.
        if (controller.signal.aborted) return
        // Задача могла не пережить перезапуск сервера — молча забываем.
        store(projectId, null)
      })
    return () => controller.abort()
  }, [projectId])

  // ------------------------------------------------------------------ WS

  useEffect(() => {
    if (!socket) return
    return socket.onEvent((event) => {
      const current = analysisRef.current
      if (!current) return
      if (!('analysisId' in event) || event.analysisId !== current.id) return
      if (event.type === 'analysis.progress') {
        setAnalysis({
          ...current,
          status: 'running',
          stage: event.stage || current.stage,
          progress: Number.isFinite(event.progress) ? event.progress : current.progress,
          message: event.message ?? current.message,
        })
      } else if (event.type === 'analysis.done') {
        void api
          .getAnalysis(current.id)
          .then((next) => {
            if (mountedRef.current) setAnalysis(next)
          })
          .catch(() => {
            if (mountedRef.current) {
              setAnalysis({ ...current, status: 'done', progress: 1, message: '' })
            }
          })
      } else if (event.type === 'analysis.error') {
        setAnalysis({ ...current, status: 'error', error: event.error })
      }
    })
  }, [socket])

  // -------------------------------------------------------------- поллинг

  const activeId =
    analysis && (analysis.status === 'queued' || analysis.status === 'running') ? analysis.id : null

  useEffect(() => {
    if (activeId === null) return
    let stopped = false
    const timer = window.setInterval(() => {
      void api
        .getAnalysis(activeId)
        .then((next) => {
          if (stopped || !mountedRef.current) return
          setAnalysis(next)
        })
        .catch(() => undefined)
    }, POLL_INTERVAL_MS)
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [activeId])

  // ------------------------------------------------------------ результат

  const fetchResult = useCallback(
    (id: string, signal?: AbortSignal): Promise<void> => {
      setFetchingResult(true)
      return api
        .getAnalysisResult(id, signal)
        .then((result: AnalysisResult) => {
          if (!mountedRef.current || signal?.aborted) return
          setDraftState(draftFromResult(result, translateRef.current))
          setError(null)
        })
        .catch((err: unknown) => {
          if (!mountedRef.current || signal?.aborted) return
          reportRequestFailure(err)
          setError(missingEndpointMessage(err) ?? describeError(err))
        })
        .finally(() => {
          if (mountedRef.current) setFetchingResult(false)
        })
    },
    [],
  )

  const doneId = analysis?.status === 'done' ? analysis.id : null

  useEffect(() => {
    if (doneId === null || draft !== null) return
    const controller = new AbortController()
    void fetchResult(doneId, controller.signal)
    return () => controller.abort()
  }, [doneId, draft, fetchResult])

  // Задача завершилась — идентификатор больше не нужен для восстановления.
  useEffect(() => {
    if (analysis && isFinished(analysis.status) && analysis.status !== 'done') {
      store(projectId, null)
    }
  }, [analysis, projectId])

  // -------------------------------------------------------------- команды

  const start = useCallback(
    async (next: AnalysisOptions): Promise<void> => {
      setStarting(true)
      setError(null)
      setDraftState(null)
      translateRef.current = next.translate
      try {
        const created = await api.analyze(projectId, next)
        if (!mountedRef.current) return
        setAnalysis(created)
        setOptions(next)
        store(projectId, { id: created.id, translate: next.translate })
      } catch (err) {
        reportRequestFailure(err)
        if (mountedRef.current) {
          setError(
            missingEndpointMessage(err) ?? segmentationRejected(err, next) ?? describeError(err),
          )
        }
        throw err
      } finally {
        if (mountedRef.current) setStarting(false)
      }
    },
    [projectId],
  )

  const cancel = useCallback(async (): Promise<void> => {
    const current = analysisRef.current
    if (!current) return
    setCancelling(true)
    try {
      await api.cancelAnalysis(current.id)
      if (!mountedRef.current) return
      setAnalysis({ ...current, status: 'cancelled', message: 'Отменено' })
      store(projectId, null)
    } catch (err) {
      reportRequestFailure(err)
      if (mountedRef.current) setError(describeError(err))
    } finally {
      if (mountedRef.current) setCancelling(false)
    }
  }, [projectId])

  const reloadResult = useCallback(async (): Promise<void> => {
    const current = analysisRef.current
    if (!current || current.status !== 'done') return
    await fetchResult(current.id)
  }, [fetchResult])

  const dismiss = useCallback((): void => {
    setAnalysis(null)
    setDraftState(null)
    setError(null)
    store(projectId, null)
  }, [projectId])

  const apply = useCallback(
    async (choices: ApplyChoices, videoDuration: number): Promise<ProjectFull | null> => {
      const current = analysisRef.current
      if (!current || !draft) return null
      const plan = buildApplyPlan(draft, videoDuration)
      setApplying(true)
      setError(null)
      try {
        const full = await api.applyAnalysis(current.id, {
          roles: plan.roles,
          lines: plan.lines,
          tracks: choices.tracks,
          replaceExisting: choices.replaceExisting,
          setModeReplace: choices.setModeReplace,
        })
        if (mountedRef.current) {
          setDraftState(null)
          setAnalysis(null)
        }
        store(projectId, null)
        return full
      } catch (err) {
        reportRequestFailure(err)
        if (mountedRef.current) setError(missingEndpointMessage(err) ?? describeError(err))
        throw err
      } finally {
        if (mountedRef.current) setApplying(false)
      }
    },
    [draft, projectId],
  )

  const setDraft = useCallback((next: AnalysisDraft): void => {
    setDraftState(next)
  }, [])

  return {
    analysis,
    draft,
    options,
    starting,
    cancelling,
    fetchingResult,
    applying,
    error,
    start,
    cancel,
    reloadResult,
    setDraft,
    dismiss,
    apply,
  }
}
